import { faClock } from '@fortawesome/free-regular-svg-icons'
import { faFlag } from '@fortawesome/free-regular-svg-icons'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { FC, ReactNode } from 'react'
import styled from 'styled-components'

import Avatar from '@/components/Avatar'
import Card from '@/components/Card'
import type { ReferenceTier } from '@/lib/apollo/query/get-applicant'
import type { Milestone } from '@/types/Activity'
import type { Address } from '@/utils/address'
import { getAddressFirstLine, getAddressSecondLine } from '@/utils/address'
import {
  getQueueDayZeroBeginning,
  getReopenQueueDayZeroBeginning,
  hasAgeOfReferenceWarning as getHasAgeOfReferenceWarning,
  hasReopenAgeOfReferenceWarning as getHasReopenAgeOfReferenceWarning,
  toAgeOfReferenceLabel,
} from '@/utils/ageOfReference'
import { formatDate, toTodayOrXDaysAgo } from '@/utils/date'
import { Colours } from '@/utils/styles'
import { toReferenceTierLabel } from '@/utils/tier'

const CardContainer = styled(Card)<{ warning: boolean }>`
  display: grid;
  grid-template-columns: min-content 1fr 1.5fr;
  grid-column-gap: 10px;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
  background: ${props => (props.warning ? Colours.lightOrange : 'inherit')};

  &:hover {
    cursor: pointer;
    background: ${props => (props.warning ? Colours.hoverOrange : Colours.hoverGrey)};
  }
`

const NameContainer = styled.div`
  font-weight: 600;
  font-size: 16px;
`

const DetailsContainer = styled.div`
  display: grid;
  grid-row-gap: 10px;
`

const DetailContainer = styled.div`
  display: grid;
  grid-template-columns: 25px 1fr;
`

const TypeContainer = styled.span`
  text-transform: capitalize;
`

const Icon = styled.img`
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  min-height: 12px;
  max-height: 12px;
  vertical-align: middle;
  margin-top: 4px;
`

const Capitalize = styled.div`
  text-transform: capitalize;
`

const getTypeLabel = (type: string): string => (type === 'applicant' ? 'tenant' : type)

interface Props {
  avatarUrl?: string
  name: string
  email: string
  type: string
  onClick: () => void
  onAuxClick: () => void
  address?: Address
  milestones?: Milestone[]
  tenancyStartDate?: Date
  visitorPanel?: ReactNode
  customerClientName?: string
  referenceTier: ReferenceTier
  flaggedAsFraudAt?: Date
}

const SubjectCard: FC<Props> = ({
  avatarUrl,
  name,
  email,
  type,
  onClick,
  onAuxClick,
  address,
  milestones,
  tenancyStartDate,
  visitorPanel,
  customerClientName,
  referenceTier,
  flaggedAsFraudAt,
}) => {
  const queueDayZeroBeginning = getQueueDayZeroBeginning(milestones)

  const reopenQueueDayZeroBeginning = getReopenQueueDayZeroBeginning(milestones)

  const hasAgeOfReferenceWarning = getHasAgeOfReferenceWarning(
    queueDayZeroBeginning,
    reopenQueueDayZeroBeginning,
  )
  const hasReopenAgeOfReferenceWarning = getHasReopenAgeOfReferenceWarning(
    reopenQueueDayZeroBeginning,
  )

  return (
    <CardContainer
      warning={hasAgeOfReferenceWarning || hasReopenAgeOfReferenceWarning}
      onClick={() => onClick()}
      onAuxClick={() => onAuxClick()}
    >
      <Avatar name={name} />
      <DetailsContainer>
        <NameContainer>{name}</NameContainer>
        {flaggedAsFraudAt && (
          <DetailContainer>
            <FontAwesomeIcon size="1x" icon={faFlag} />
            <div>
              {toTodayOrXDaysAgo(flaggedAsFraudAt)} ({formatDate(flaggedAsFraudAt)} at{' '}
              {formatDate(flaggedAsFraudAt, 'HH:mm')})
            </div>
          </DetailContainer>
        )}
        {address && (
          <DetailContainer>
            <Icon src="/assets/svg/residential.svg" alt="icon of a house" />
            <Capitalize>
              <>
                <div>{getAddressFirstLine(address)}</div>
                <div>{getAddressSecondLine(address)}</div>
              </>
            </Capitalize>
          </DetailContainer>
        )}
        <DetailContainer>
          <Icon
            src="/assets/svg/envelope.svg"
            alt="icon of a small envelope with a black outline"
          />
          <div>{email}</div>
        </DetailContainer>
        <DetailContainer>
          <Icon src="/assets/svg/guarantor.svg" alt="icon of a person" />
          <div>
            <TypeContainer>{getTypeLabel(type)}</TypeContainer> |{' '}
            {toReferenceTierLabel(referenceTier)}
          </div>
        </DetailContainer>
      </DetailsContainer>
      <DetailsContainer>
        {tenancyStartDate && (
          <DetailContainer>
            <Icon src="/assets/svg/help-the-move.svg" alt="icon of a person" />
            <div>Tenancy start date: {formatDate(tenancyStartDate)}</div>
          </DetailContainer>
        )}
        <DetailContainer>
          <FontAwesomeIcon
            size="1x"
            icon={faClock}
            color={hasAgeOfReferenceWarning ? Colours.orange : 'inherit'}
          />
          <div>Age of reference: {toAgeOfReferenceLabel(queueDayZeroBeginning)}</div>
        </DetailContainer>
        {reopenQueueDayZeroBeginning && (
          <DetailContainer>
            <FontAwesomeIcon
              size="1x"
              icon={faRotate}
              color={hasReopenAgeOfReferenceWarning ? Colours.orange : 'inherit'}
            />
            <div>
              Age of reference since reopening:{' '}
              {toAgeOfReferenceLabel(reopenQueueDayZeroBeginning)}
            </div>
          </DetailContainer>
        )}
        {customerClientName && (
          <DetailContainer>
            <Icon src="/assets/svg/user.svg" alt="icon of a person" />
            <div>Agency: {customerClientName}</div>
          </DetailContainer>
        )}
      </DetailsContainer>
      <div></div>
      {visitorPanel}
    </CardContainer>
  )
}

export default SubjectCard
